<template>
  <div class="quota-order-status">
    <div class="status-summary">
      <div class="status-name">
        <svg
          class="status-icon"
          aria-hidden="true"
        >
          <use xlink:href="#icon-daichuli" />
        </svg>
        <strong>{{ statusText }}</strong>
        <span
          v-if="orderInfo.remark"
          class="remark"
        >{{ orderInfo.remark }}</span>
      </div>
      <div class="summary-item">
        <span>业务流水号：{{ orderNo }}</span>
        <span>担保金额：￥{{ orderInfo.sumAmount || 0 }} 元</span>
      </div>
      <div class="summary-item">
        <span>申请时间：{{ formatDate(orderInfo.createDate) }}</span>
        <span class="premium">应付保费：￥{{ orderInfo.sumPremium || 0 }} 元</span>
      </div>
    </div>

    <div class="actions">
      <a-button
        v-if="canPay"
        type="primary"
        @click="$emit('pay', orderInfo.payUrl)"
      >
        支付
      </a-button>
      <a-button
        v-if="canGiveUp"
        @click="$emit('giveup')"
      >
        取消投保
      </a-button>
      <a-button
        v-if="canSurrender"
        @click="surrenderVisible = true"
      >
        申请退保
      </a-button>
      <a-button
        v-if="!isHamiChannel && canInvoice"
        @click="openInvoice"
      >
        开票
      </a-button>
      <a-button
        v-if="!isHamiChannel && orderInfo.invoiceUrl"
        @click="$emit('open-url', orderInfo.invoiceUrl)"
      >
        下载发票
      </a-button>
      <a-button
        v-if="guaranteeUrl"
        type="primary"
        @click="$emit('open-url', guaranteeUrl)"
      >
        下载保函
      </a-button>
      <a-button
        v-if="!isHamiChannel"
        @click="$emit('refresh')"
      >
        刷新状态
      </a-button>
    </div>

    <PlateTitle title="投保信息" />
    <a-form
      :label-col="{ span: 6 }"
      :wrapper-col="{ span: 16 }"
      class="detail-form"
    >
      <a-form-item label="投标人名称">
        {{ orderInfo.name }}
      </a-form-item>
      <a-form-item label="统一社会信用代码">
        {{ orderInfo.creditCode }}
      </a-form-item>
      <a-form-item label="标段名称">
        {{ orderInfo.sectionName }}
      </a-form-item>
      <a-form-item label="标段编号">
        {{ orderInfo.sectionCode }}
      </a-form-item>
      <a-form-item label="招标人名称">
        {{ orderInfo.tendereeName }}
      </a-form-item>
      <a-form-item label="出函机构">
        {{ orderInfo.partnerLogoName || orderInfo.partnerName }}
      </a-form-item>
      <a-form-item label="经办人">
        {{ orderInfo.responsiblePersonName }}
      </a-form-item>
      <a-form-item label="联系方式">
        {{ orderInfo.responsiblePersonPhone }}
      </a-form-item>
    </a-form>

    <a-modal
      v-model="surrenderVisible"
      title="申请退保"
      ok-text="确认申请"
      cancel-text="取消"
      :confirm-loading="submitting"
      @ok="submitSurrender"
    >
      <a-form
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-item
          label="退保原因"
          required
        >
          <a-select
            v-model="surrenderReason"
            style="width: 100%"
          >
            <a-select-option value="项目未开标，不想投标了">
              项目未开标，不想投标了
            </a-select-option>
            <a-select-option value="投标人失误，或选择投标项目错误">
              投标人失误，或选择投标项目错误
            </a-select-option>
            <a-select-option value="因项目变化等其他原因">
              因项目变化等其他原因
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      v-model="invoiceVisible"
      title="申请开票"
      ok-text="确认申请"
      cancel-text="取消"
      :confirm-loading="submitting"
      @ok="submitInvoice"
    >
      <a-form
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-item label="发票抬头">
          {{ orderInfo.invoiceHead || orderInfo.name }}
        </a-form-item>
        <a-form-item
          label="发票类型"
          required
        >
          <a-select
            v-model="invoiceForm.invoiceType"
            style="width: 100%"
          >
            <a-select-option value="2">
              电子普通发票
            </a-select-option>
            <a-select-option value="3">
              电子专用发票
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item
          label="电子邮箱"
          required
        >
          <a-input v-model.trim="invoiceForm.email" />
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea
            v-model="invoiceForm.remark"
            :rows="3"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import PlateTitle from '@/components/PlateTitle';
import { dateFormat } from '@/utils/format';

export default {
  name: 'QuotaOrderStatus',
  components: { PlateTitle },
  props: {
    orderInfo: { type: Object, default: () => ({}) },
    orderNo: { type: String, required: true },
    channelCode: { type: String, default: '' },
    submitting: { type: Boolean, default: false },
    giveUpEnabled: { type: Boolean, default: true },
  },
  data() {
    return {
      surrenderVisible: false,
      surrenderReason: '',
      invoiceVisible: false,
      invoiceForm: { invoiceType: '2', email: '', remark: '' },
    };
  },
  computed: {
    isHamiChannel() {
      return this.channelCode === 'hami';
    },
    statusText() {
      return this.orderInfo.statusStr || this.orderInfo.guaranteeStatusStr || '处理中';
    },
    canPay() {
      return [ '11', '13' ].includes(String(this.orderInfo.status)) &&
        Boolean(this.orderInfo.payBut && this.orderInfo.payUrl);
    },
    canGiveUp() {
      return !this.isHamiChannel && this.giveUpEnabled &&
        Boolean(this.orderInfo.isGiveup && this.orderInfo.giveupBut);
    },
    canSurrender() {
      return !this.isHamiChannel &&
        Boolean(this.orderInfo.isInsureCancel && this.orderInfo.surrenderBut);
    },
    canInvoice() {
      return Boolean(this.orderInfo.isInsureInvoice && this.orderInfo.invoiceBut);
    },
    guaranteeUrl() {
      return this.orderInfo.signDownloadUrl || this.orderInfo.preSignDownloadUrl || '';
    },
  },
  methods: {
    formatDate(value) {
      return value ? dateFormat(value) : '-';
    },
    submitSurrender() {
      if (!this.surrenderReason) {
        this.$message.warning('请选择退保原因');
        return;
      }
      this.$emit('surrender', { cancelReason: this.surrenderReason });
      this.surrenderVisible = false;
    },
    openInvoice() {
      this.invoiceForm.email = this.orderInfo.invoiceEmail || '';
      this.invoiceForm.invoiceType = String(this.orderInfo.invoiceType || '2');
      this.invoiceVisible = true;
    },
    submitInvoice() {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.invoiceForm.email || '')) {
        this.$message.warning('请输入正确的电子邮箱');
        return;
      }
      this.$emit('invoice', {
        invoiceType: this.invoiceForm.invoiceType,
        invoiceHead: this.orderInfo.invoiceHead || this.orderInfo.name,
        cuscCode: this.orderInfo.creditCode,
        email: this.invoiceForm.email,
        remark: this.invoiceForm.remark,
      });
      this.invoiceVisible = false;
    },
  },
};
</script>

<style lang="less" scoped>
.status-summary {
  display: flex;
  align-items: center;
  margin: 20px 0 26px;
  padding: 24px;
  background: #fafafa;
  .status-name, .summary-item { display: flex; flex: 1; flex-direction: column; gap: 8px; }
  .status-name { align-items: center; }
  .status-icon { width: 72px; height: 72px; fill: currentColor; }
  .remark { color: #d4380d; }
  .premium { color: #d9001b; font-size: 18px; }
}
.actions { margin-bottom: 30px; text-align: center; button { margin: 0 6px; } }
.detail-form { margin-top: 20px; }
</style>
